import { useParams, useHistory } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../services/firebase.config';

import { makeStyles, Theme, Container, Typography, Box, Card, Button, IconButton, Fade } from '@material-ui/core';
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Note as NoteIcon,
  PhotoCamera as PhotoIcon,
  Settings as AIIcon,
  ExitToApp as LogoutIcon,
} from '@material-ui/icons';

import JobFeed from '../JobFeed/JobFeed';
import AddTextItemForm from '../AddTextItemForm/AddTextItemForm';
import AddAIItemForm from '../AddAIItemForm/AddAIItemForm';
import AddImageItemForm from '../AddImageItemForm/AddImageItemForm';
import { onAuthStateChanged, User, signOut } from 'firebase/auth';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    backgroundColor: 'white',
    padding: theme.spacing(2, 0),
    marginBottom: theme.spacing(3),
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    borderBottom: '1px solid #e9ecef',
    flexShrink: 0,
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: '1400px',
    margin: '0 auto',
    padding: theme.spacing(0, 3),
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  backButton: {
    color: '#2980b9',
    '&:hover': {
      backgroundColor: 'rgba(41, 128, 185, 0.1)',
    },
  },
  logo: {
    color: '#2c3e50',
    fontWeight: 'bold',
    fontSize: '1.2rem',
    textDecoration: 'none',
    '&:hover': {
      color: '#2980b9',
    },
  },
  userInfo: {
    color: '#6c757d',
    fontSize: '0.9rem',
  },
  logoutButton: {
    color: '#6c757d',
    '&:hover': {
      color: '#2980b9',
      backgroundColor: 'rgba(41, 128, 185, 0.1)',
    },
  },
  title: {
    color: '#2c3e50',
    fontWeight: 'bold',
    fontSize: '1.8rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.4rem',
    },
  },
  jobMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
    },
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    color: '#6c757d',
    fontSize: '0.9rem',
    gap: theme.spacing(0.5),
  },
  metaIcon: {
    fontSize: '1rem',
    color: '#95a5a6',
  },
  mainContainer: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: theme.spacing(0, 3),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: '400px 1fr',
    gap: theme.spacing(3),
    flex: 1,
    alignItems: 'start',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(2),
    },
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  liveCard: {
    backgroundColor: 'white',
    borderRadius: theme.spacing(1.5),
    border: '1px solid #e9ecef',
    padding: theme.spacing(3),
  },
  liveButtonContainer: {
    '& > div': {
      width: '100%',
    },
    '& button': {
      backgroundColor: '#2980b9',
      color: 'white',
      fontWeight: 'bold',
      textTransform: 'none',
      borderRadius: theme.spacing(1),
      padding: theme.spacing(1.5, 3),
      fontSize: '1rem',
      width: '100%',
      '&:hover': {
        backgroundColor: '#3498db',
      },
    },
  },
  actionsCard: {
    backgroundColor: 'white',
    borderRadius: theme.spacing(1.5),
    border: '1px solid #e9ecef',
    padding: theme.spacing(3),
  },
  sectionTitle: {
    color: '#2c3e50',
    fontWeight: 'bold',
    fontSize: '1.2rem',
    marginBottom: theme.spacing(2),
  },
  actionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  actionItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.spacing(1),
    border: '1px solid #e9ecef',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#3498db',
      backgroundColor: '#f8f9fa',
      transform: 'translateX(4px)',
    },
  },
  activeActionItem: {
    borderColor: '#2980b9',
    backgroundColor: 'rgba(41, 128, 185, 0.05)',
  },
  actionIcon: {
    fontSize: '1.5rem',
    color: '#3498db',
    marginRight: theme.spacing(1.5),
  },
  actionText: {
    color: '#2c3e50',
    fontWeight: 500,
    fontSize: '0.95rem',
  },
  feedCard: {
    backgroundColor: 'white',
    borderRadius: theme.spacing(1.5),
    border: '1px solid #e9ecef',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  feedHeader: {
    padding: theme.spacing(3, 3, 2, 3),
    borderBottom: '1px solid #f1f2f6',
    flexShrink: 0,
  },
  feedContent: {
    padding: theme.spacing(2, 3, 3, 3),
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  formCard: {
    backgroundColor: 'white',
    borderRadius: theme.spacing(1.5),
    border: '1px solid #e9ecef',
    padding: theme.spacing(3),
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  formTitle: {
    color: '#2c3e50',
    fontWeight: 'bold',
    fontSize: '1.1rem',
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
  },
  loadingCard: {
    padding: theme.spacing(4),
    textAlign: 'center',
    borderRadius: theme.spacing(2),
    backgroundColor: 'white',
    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
  },
}));

enum FormType {
  None,
  Text,
  ImageOrVideo,
  AI,
}

function JobPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const history = useHistory();
  const [jobData, setJobData] = useState<any>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userName, setUserName] = useState('');
  const [activeForm, setActiveForm] = useState<FormType>(FormType.None);

  const classes = useStyles();

  useEffect(() => {
    const authStateListener = onAuthStateChanged(auth, currentUser => {
      if (currentUser) {
        setUser(currentUser);
        console.log('uid', currentUser.uid);
      } else {
        console.log('user is logged out');
      }
    });

    return () => {
      authStateListener();
    };
  }, []);

  useEffect(() => {
    const fetchJob = async () => {
      const docRef = doc(db, 'job', jobId);
      const jobSnapshot = await getDoc(docRef);

      if (jobSnapshot.exists()) {
        const fetchedJobData = jobSnapshot.data();
        setJobData(fetchedJobData);

        // Fetch user's name using userId from the job data
        const userDoc = await getDoc(doc(db, 'users', fetchedJobData.userId));
        if (userDoc.exists()) {
          setUserName(userDoc.data().name);
        }
      }
    };

    fetchJob();
  }, [jobId]);

  const handleActionClick = (formType: FormType) => {
    setActiveForm(prev => (prev === formType ? FormType.None : formType));
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        history.replace('/');
        console.log('Signed out successfully');
      })
      .catch(error => {
        console.error('Error signing out:', error);
      });
  };

  const handleJoinRoom = () => {
    const escapedRoomName = encodeURIComponent(jobData.job);
    const escapedDisplayName = encodeURIComponent(user?.displayName || 'Technician');
    const newLocation = `/room/${jobId}/${escapedRoomName}/${escapedDisplayName}`;
    console.log('Joining room:', newLocation);
    history.push(newLocation);
  };

  const getFormTitle = () => {
    switch (activeForm) {
      case FormType.Text:
        return 'Add Note';
      case FormType.ImageOrVideo:
        return 'Add Image or Video';
      case FormType.AI:
        return 'Ask AI Assistant';
      default:
        return '';
    }
  };

  if (!jobData) {
    return (
      <div className={classes.root}>
        <Box className={classes.loadingContainer}>
          <Card className={classes.loadingCard}>
            <Typography variant="h5" style={{ color: '#2c3e50', marginBottom: 16 }}>
              Loading job details...
            </Typography>
            <Typography variant="body1" style={{ color: '#6c757d' }}>
              Please wait while we fetch the job information.
            </Typography>
          </Card>
        </Box>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* Custom Header */}
      <Box className={classes.header}>
        <Box className={classes.headerContent}>
          <Box className={classes.headerLeft}>
            <IconButton className={classes.backButton} onClick={() => history.goBack()}>
              <ArrowBackIcon />
            </IconButton>
            <Typography component="a" href="/dashboard" className={classes.logo}>
              BLUESTREAM
            </Typography>
            <Box>
              <Typography variant="h4" className={classes.title}>
                {jobData.job}
              </Typography>
              <Box className={classes.jobMeta}>
                <Box className={classes.metaItem}>
                  <LocationIcon className={classes.metaIcon} />
                  {jobData.address}
                </Box>
                <Box className={classes.metaItem}>
                  <TimeIcon className={classes.metaIcon} />
                  {new Date(jobData.timestamp?.seconds * 1000).toLocaleDateString()}
                </Box>
                <Box className={classes.metaItem}>
                  <PersonIcon className={classes.metaIcon} />
                  {userName}
                </Box>
              </Box>
            </Box>
          </Box>

          <Box className={classes.headerRight}>
            {user && (
              <>
                <Typography className={classes.userInfo}>{user.displayName || user.email}</Typography>
                <IconButton className={classes.logoutButton} onClick={handleLogout} title="Logout">
                  <LogoutIcon />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Container maxWidth={false} className={classes.mainContainer}>
        {/* Main Content Grid */}
        <Box className={classes.contentGrid}>
          {/* Left Column - Sidebar */}
          <Box className={classes.leftColumn}>
            {/* Live Collaboration */}
            <Card className={classes.liveCard}>
              <Typography className={classes.sectionTitle}>Live Collaboration</Typography>
              <Typography variant="body2" style={{ color: '#6c757d', marginBottom: 16 }}>
                Connect with technicians in real-time
              </Typography>
              <Box className={classes.liveButtonContainer}>
                <Button
                  onClick={handleJoinRoom}
                  variant="contained"
                  size="large"
                  style={{
                    backgroundColor: '#2980b9',
                    color: 'white',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    borderRadius: 8,
                    padding: '12px 24px',
                    fontSize: '1rem',
                    width: '100%',
                  }}
                >
                  Start Live Session
                </Button>
              </Box>
            </Card>

            {/* Job Tools */}
            <Card className={classes.actionsCard}>
              <Typography className={classes.sectionTitle}>Job Tools</Typography>
              <Box className={classes.actionsList}>
                <Box
                  className={`${classes.actionItem} ${activeForm === FormType.Text ? classes.activeActionItem : ''}`}
                  onClick={() => handleActionClick(FormType.Text)}
                >
                  <NoteIcon className={classes.actionIcon} />
                  <Typography className={classes.actionText}>Add Note</Typography>
                </Box>

                <Box
                  className={`${classes.actionItem} ${
                    activeForm === FormType.ImageOrVideo ? classes.activeActionItem : ''
                  }`}
                  onClick={() => handleActionClick(FormType.ImageOrVideo)}
                >
                  <PhotoIcon className={classes.actionIcon} />
                  <Typography className={classes.actionText}>Add Photo/Video</Typography>
                </Box>

                <Box
                  className={`${classes.actionItem} ${activeForm === FormType.AI ? classes.activeActionItem : ''}`}
                  onClick={() => handleActionClick(FormType.AI)}
                >
                  <AIIcon className={classes.actionIcon} />
                  <Typography className={classes.actionText}>Ask AI Assistant</Typography>
                </Box>
              </Box>
            </Card>

            {/* Active Form */}
            {activeForm !== FormType.None && (
              <Fade in={true}>
                <Card className={classes.formCard}>
                  <Typography className={classes.formTitle}>{getFormTitle()}</Typography>
                  {activeForm === FormType.Text && user && <AddTextItemForm jobId={jobId} userId={user.uid} />}
                  {activeForm === FormType.ImageOrVideo && user && <AddImageItemForm jobId={jobId} userId={user.uid} />}
                  {activeForm === FormType.AI && user && <AddAIItemForm jobId={jobId} userId={user.uid} />}
                </Card>
              </Fade>
            )}
          </Box>

          {/* Right Column - Main Content (Activity Feed) */}
          <Box className={classes.rightColumn}>
            <Card className={classes.feedCard}>
              <Box className={classes.feedHeader}>
                <Typography className={classes.sectionTitle}>Job Activity Feed</Typography>
                <Typography variant="body2" style={{ color: '#6c757d' }}>
                  Real-time updates, notes, images, and AI insights for this job
                </Typography>
              </Box>
              <Box className={classes.feedContent}>
                <JobFeed jobId={jobId} />
              </Box>
            </Card>
          </Box>
        </Box>
      </Container>
    </div>
  );
}

export default JobPage;
